import React, { useState } from "react";

import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";

import { FrostFreeStatus, type PatchBuildingRoomsRequestBody } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { ActionButton, Alert, Steps, Switch } from "@eisox/design-system";
import { ArrowRightIcon } from "@eisox/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { roomApi } from "~/api";
import {
  frostFreePeriodsAllowed,
  FrostFreeSelect,
  getDefaultMode,
  getFrostFreeModes,
  Periods,
} from "~/features/frost-free";
import {
  buildPeriodPreviewRooms,
  defaultBulkFrostFreeMode,
  getRoomPatchFromRooms,
  roomsHolidaysSettingsSchema,
  RoomsSelector,
  type Filters,
  type RoomsHolidaysSettingsSchemaType,
} from "~/features/settings";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/settings/rooms/holidays")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const { building, floors, rooms, gateways } = useBuildingContext(Route.id);

  const periodsAllowed = frostFreePeriodsAllowed(gateways);

  const defaultValues: RoomsHolidaysSettingsSchemaType = {
    roomIds: [],
    mode: getDefaultMode(undefined, getFrostFreeModes({ periodsAllowed })),
    frostFreePeriods: [],
  };

  const { mutate: editRooms } = useMutation({
    mutationFn: (body: PatchBuildingRoomsRequestBody) => roomApi.update(building.id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roomApi.queries.building(building.id).queryKey });
      setFilters({});
      reset(defaultValues);

      setMerge(true);
    },
  });

  const [filters, setFilters] = useState<Filters>({});
  const [merge, setMerge] = useState(true);

  const {
    control,
    formState: {
      isDirty,
      errors: { frostFreePeriods: frostFreePeriodsErrors },
    },
    reset,
    handleSubmit,
  } = useForm<RoomsHolidaysSettingsSchemaType>({
    resolver: zodResolver(roomsHolidaysSettingsSchema),
    defaultValues,
  });

  const roomIds = useWatch({ control, name: "roomIds" });
  const mode = useWatch({ control, name: "mode" });
  const currentFrostFreePeriods = useWatch({ control, name: "frostFreePeriods" });

  const { fields, append, remove, update } = useFieldArray({ control, name: "frostFreePeriods" });

  const onSubmit = (data: RoomsHolidaysSettingsSchemaType) => {
    editRooms(getRoomPatchFromRooms(data.mode, data.roomIds, periodPreviewRooms));
  };

  const selectedRooms = rooms.filter(room => roomIds.includes(room.id));
  const periodPreviewRooms =
    mode === FrostFreeStatus.PERIODS ? buildPeriodPreviewRooms(selectedRooms, merge, currentFrostFreePeriods) : [];

  return (
    <form
      className="gap-5 flex h-full flex-col overflow-auto"
      data-testid="RoomsHolidaysSettings"
      onSubmit={e => void handleSubmit(onSubmit)(e)}
    >
      <Alert severity="help">
        {m.frost_free_could_be_controlled_by_building()}
        <Link
          to={"/buildings/$buildingId/settings/buildings/holidays"}
          params={{ buildingId: building.id }}
          className="text-base text-primary-blue"
        >
          {m.see_parameter()}
        </Link>
      </Alert>
      <Steps.Root>
        <Steps.Step>
          <Steps.Title>{m.select_places({ resource: "room" })}</Steps.Title>
          <Steps.Content>
            <Controller
              control={control}
              name="roomIds"
              render={({ field: { onChange } }) => (
                <RoomsSelector.Root
                  rooms={rooms}
                  onFilteredRoomsChange={filteredRooms => {
                    const ids = filteredRooms.map(r => r.id);
                    onChange(ids);
                    reset({
                      roomIds: ids,
                      mode: defaultBulkFrostFreeMode(filteredRooms, periodsAllowed),
                      frostFreePeriods: [],
                    });
                  }}
                  value={filters}
                  onValueChange={setFilters}
                >
                  <div className="*:min-w-0 mb-[10px] flex gap-[10px] *:flex-1">
                    <RoomsSelector.TypeSearch />
                    <RoomsSelector.PlanSearch floors={floors} />
                    <RoomsSelector.GroupSearch />
                    <RoomsSelector.PersonnalizedSearch floors={floors} />
                  </div>
                  <RoomsSelector.Result />
                </RoomsSelector.Root>
              )}
            />
            <div className="gap-x-10 mt-[10px] grid max-h-[240px] w-fit grid-cols-[max-content_auto] overflow-auto text-base">
              {selectedRooms.map((r, i) => (
                <React.Fragment key={i}>
                  <p>{r.name}</p>
                  <p>
                    {r.frostFree.periods
                      .map(p => `${dayjs(p.begin).format("L")} - ${dayjs(p.end).format("L")}`)
                      .join(", ")}
                  </p>
                </React.Fragment>
              ))}
            </div>
          </Steps.Content>
        </Steps.Step>
        <Steps.Step>
          <Steps.Title>{m.frost_free_mode()}</Steps.Title>
          <Steps.Content className="gap-2.5 flex flex-col">
            <Controller
              control={control}
              name="mode"
              render={({ field: { value, onChange } }) => (
                <FrostFreeSelect
                  value={value}
                  onChange={onChange}
                  modes={getFrostFreeModes({ periodsAllowed })}
                  className="w-[263px]"
                />
              )}
            />
            {mode === FrostFreeStatus.PERIODS && (
              <>
                <label className="flex w-fit items-center gap-x-[10px] text-base">
                  {m.erase_old_periods()}
                  <Switch checked={!merge} onCheckedChange={checked => setMerge(!checked)} />
                </label>
                <Periods
                  className="mt-[10px] max-w-[475px]"
                  periods={fields.map((f, i) => ({ ...f, error: frostFreePeriodsErrors?.[i]?.message }))}
                  onAddPeriod={append}
                  onRemovePeriod={remove}
                  onUpdatePeriod={update}
                />
              </>
            )}

            <div className="gap-x-10 mt-[10px] grid max-h-[240px] w-fit grid-cols-[max-content_auto] overflow-auto text-base">
              {selectedRooms.map((r, i) => (
                <React.Fragment key={r.id}>
                  <p>{r.name}</p>
                  <p>
                    {mode === FrostFreeStatus.PERIODS
                      ? periodPreviewRooms[i]?.frostFree.periods
                          .map(p => `${dayjs(p.begin).format("L")} - ${dayjs(p.end).format("L")}`)
                          .join(", ")
                      : m.activation({ activation: mode === FrostFreeStatus.ENABLED ? "true" : "false" })}
                  </p>
                </React.Fragment>
              ))}
            </div>
          </Steps.Content>
        </Steps.Step>
        <Steps.Step>
          <Steps.Title>{m.save_parameters()}</Steps.Title>
          <Steps.Content>
            <ActionButton type="submit" disabled={!isDirty}>
              {m.save()} <ArrowRightIcon />
            </ActionButton>
          </Steps.Content>
        </Steps.Step>
      </Steps.Root>
    </form>
  );
}
